import { Box, VStack } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { useGetLoggedInUser } from '@revelio/auth';

import { SelectionListControls } from '../../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import {
  type Option,
  SelectionListSelect,
} from '../../../../../../shared/selection-lists/components/SelectionListSelect/SelectionListSelect';
import { shouldShowCurrentPreviousToggle } from '../../../config/currentPreviousConfig';
import {
  getAdditionalFilterOption,
  STATIC_FILTER_DATA,
  STATIC_FILTER_IDS,
} from '../../../constants/staticFilters';
import { useEffectiveFilters } from '../../../hooks/useEffectiveFilters';
import { FilterId, type StaticListFilter } from '../../../types/filters';
import { ToggleSelect } from '../../ToggleSelect';
import { NotToggle } from '../NotToggle';

interface StaticListFilterFormProps {
  filter: StaticListFilter;
  closeMenu: () => void;
  onSave: (filter: StaticListFilter) => void;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

export const StaticListFilterForm: React.FC<StaticListFilterFormProps> = ({
  filter,
  closeMenu,
  onSave,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}) => {
  const location = useLocation();
  const { loggedInUser } = useGetLoggedInUser();

  // Initialize from filter.selections prop directly (same strategy as TreeFilterForm and FreeTextFilterForm)
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(() => {
    return (
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
        limit: selection.limit,
      })) || []
    );
  });
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  const effectiveFilters = useEffectiveFilters();
  // Sync selectedOptions from filter.selections when filter prop changes (same pattern as TreeFilterForm)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const optionsToShow =
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
        limit: selection.limit,
      })) || [];

    if (!isEqual(selectedOptions, optionsToShow)) {
      setSelectedOptions(optionsToShow);
    }
  }, [filter.selections]);

  const options: Option[] = STATIC_FILTER_DATA[
    filter.id as keyof typeof STATIC_FILTER_DATA
  ]
    .map((item) => {
      if (
        STATIC_FILTER_IDS.includes(
          filter.id as (typeof STATIC_FILTER_IDS)[number]
        )
      ) {
        return {
          value: item.value,
          label: item.label,
          limit: item.limit,
          ...getAdditionalFilterOption({
            filter,
            allFilters: effectiveFilters,
            item,
          }),
        };
      }

      throw new Error('Invalid filter id');
    })
    // Filter out posting source linkup postings if disabled for user
    .filter((option) => {
      if (filter.id !== FilterId.POSTINGS_SOURCE) return true;
      // id 2 is linkup postings
      if (option.value !== '2') return true;
      // only allow linkup postings if user has linkup_postings set to true
      return loggedInUser.linkup_postings;
    });

  const isMulti = options[0]?.limit !== 1;

  const handleSelectionChange = (
    newValue: MultiValue<Option> | Option | null
  ) => {
    if (isMulti) {
      setSelectedOptions(Array.isArray(newValue) ? [...newValue] : []);
    } else {
      if (newValue && 'value' in newValue && 'label' in newValue) {
        setSelectedOptions([newValue]);
      } else {
        setSelectedOptions([]);
      }
    }
  };

  const handleSubmit = () => {
    const selections = selectedOptions.map((option) => ({
      id: option.value,
      name: option.label,
    }));

    // Use onSave prop (same strategy as TreeFilterForm and FreeTextFilterForm)
    // This handles multi-filter instance creation via FilterFormRender
    onSave({
      ...filter,
      selections,
      isCurrent,
      isNot: isNotEnabled,
    });
    closeMenu();
  };

  const handleClear = () => {
    setSelectedOptions([]);
  };

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  const selectValue = isMulti ? selectedOptions : (selectedOptions[0] ?? null);

  return (
    <VStack spacing={4} align="stretch">
      <VStack position="relative" spacing={2} align="stretch">
        {showNotToggle && onIsNotEnabledChange && (
          <Box position="absolute" top="34px">
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
            />
          </Box>
        )}
        <SelectionListSelect
          options={options}
          value={selectValue}
          maxMenuHeight={showNotToggle ? 180 : 220}
          onChange={handleSelectionChange}
          isMulti={isMulti}
          customStyles={{
            menu: showNotToggle
              ? {
                  margin: '2rem 0 0',
                }
              : {},
          }}
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </VStack>
      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleSubmit}
        onClose={closeMenu}
        submitText={(filter.selections?.length ?? 0) > 0 ? 'Update' : 'Add'}
        isSubmitDisabled={selectedOptions.length === 0}
      />
    </VStack>
  );
};
